import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useReadContract } from 'wagmi';
import { formatUnits } from 'viem';
import { vaultConfig } from '../config/vaultConfig-v2';
import { useAutoSwitchNetwork } from '../hooks/useAutoSwitchNetwork';
import './Vaults.css';

// Vault configurations
const vaults = [
  {
    id: 'vUSDC',
    name: 'vUSDC',
    asset: 'USDC',
    description: 'Earn yield on USDC across multiple DeFi protocols',
    icon: '/images/usdc.png', // You can place the image here
    active: false,
    route: '/vault',
    contractAddress: vaultConfig.vaultCore.address,
    color: '#2775CA',
    bgGradient: 'linear-gradient(135deg, #2775CA 0%, #1e5aa8 100%)',
  },
  {
    id: 'vUSDT',
    name: 'vUSDt',
    asset: 'USDt',
    description: 'Earn yield on Tether USD',
    icon: '/images/usdt.png',
    active: false,
    route: '/vault-usdt',
    color: '#50AF95',
    bgGradient: 'linear-gradient(135deg, #50AF95 0%, #3d8771 100%)',
  },
  {
    id: 'vBTC',
    name: 'vBTC.B',
    asset: 'BTC.B',
    description: 'Earn yield on wrapped Bitcoin',
    icon: '/images/btc.png',
    active: false,
    route: '/vault-btc',
    color: '#F7931A',
    bgGradient: 'linear-gradient(135deg, #F7931A 0%, #d47915 100%)',
  },
];

function VaultCard({ vault }) {
  const navigate = useNavigate();

  // Fetch APR for active vaults
  const { data: apr } = useReadContract({
    address: vault.contractAddress,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'getAPR',
    enabled: vault.active && vault.contractAddress,
    watch: true,
  });

  // Fetch total assets for active vaults
  const { data: totalAssets } = useReadContract({
    address: vault.contractAddress,
    abi: vaultConfig.vaultCore.abi,
    functionName: 'totalAssets',
    enabled: vault.active && vault.contractAddress,
    watch: true,
  });

  const aprValue = apr ? Number(apr) / 100 : 0;
  const tvlValue = totalAssets ? parseFloat(formatUnits(totalAssets, 6)) : 0;

  const handleClick = () => {
    if (vault.active) {
      navigate(vault.route);
    }
  };

  return (
    <div
      className={`vault-card ${vault.active ? 'active' : 'inactive'}`}
      onClick={handleClick}
      style={{
        '--vault-color': vault.color,
      }}
    >
      {!vault.active && <div className='coming-soon-badge'>Coming Soon</div>}

      <div className='vault-header'>
        <div className='vault-icon-wrapper' style={{ backgroundColor: `${vault.color}20` }}>
          {vault.icon ? (
            <img src={vault.icon} alt={vault.asset} className='vault-icon' />
          ) : (
            <div className='vault-icon-placeholder' style={{ color: vault.color }}>
              {vault.asset.slice(0, 2)}
            </div>
          )}
        </div>
        <div className='vault-title'>
          <h3>{vault.name}</h3>
          <span className='vault-asset'>{vault.asset}</span>
        </div>
      </div>

      <p className='vault-description'>{vault.description}</p>

      {vault.active ? (
        <div className='vault-stats'>
          <div className='stat'>
            <span className='stat-label'>APR</span>
            <span className='stat-value'>{aprValue.toFixed(2)}%</span>
          </div>
          <div className='stat'>
            <span className='stat-label'>TVL</span>
            <span className='stat-value'>
              ${tvlValue >= 1000000 
                ? `${(tvlValue / 1000000).toFixed(1)}M`
                : tvlValue >= 1000 
                ? `${(tvlValue / 1000).toFixed(1)}K`
                : tvlValue.toFixed(0)}
            </span>
          </div>
        </div>
      ) : (
        <div className='vault-stats coming-soon'>
          <div className='stat'>
            <span className='stat-label'>APR</span>
            <span className='stat-value'>--</span>
          </div>
          <div className='stat'>
            <span className='stat-label'>TVL</span>
            <span className='stat-value'>--</span>
          </div>
        </div>
      )}

      {vault.active && <button className='vault-enter-btn'>Enter Vault →</button>}
    </div>
  );
}

function Vaults() {
  useAutoSwitchNetwork(); // Auto-switch to Avalanche
  
  return (
    <div className='vaults-container'>
      <div className='vaults-header'>
        <h1>Velvet Vaults</h1>
        <p className='vaults-subtitle'>Diversified yield strategies across multiple DeFi protocols</p>
      </div>

      <div className='vaults-grid'>
        {vaults.map((vault) => (
          <VaultCard key={vault.id} vault={vault} />
        ))}
      </div>

      <div className='vaults-info'>
        <div className='info-section'>
          <h2>How Velvet Vaults Work</h2>
          <div className='info-grid'>
            <div className='info-item'>
              <div className='info-number'>1</div>
              <h3>Deposit Assets</h3>
              <p>Deposit your stablecoins or crypto assets into our vaults</p>
            </div>
            <div className='info-item'>
              <div className='info-number'>2</div>
              <h3>Earn Yield</h3>
              <p>Your assets are deployed across multiple DeFi protocols to maximize yield</p>
            </div>
            <div className='info-item'>
              <div className='info-number'>3</div>
              <h3>Auto-Compound</h3>
              <p>Yields are automatically harvested and reinvested for compound growth</p>
            </div>
            <div className='info-item'>
              <div className='info-number'>4</div>
              <h3>Withdraw Anytime</h3>
              <p>Redeem your shares for the underlying assets plus earned yield</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Vaults;
